import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CalendarIcon,
  KeyIcon,
  ClockIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import userService from '../../services/userService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { user: currentUser, hasPermission } = useAuth();
  const canManageUsers = hasPermission('manage_users');
  const canViewUser = canManageUsers || String(currentUser?.id) === String(id);
  
  const [user, setUser] = useState(null);
  const [activity, setActivity] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm_password: '' });

  useEffect(() => {
    if (canViewUser) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [id, canViewUser]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const userData = await userService.getUser(id);
      setUser(userData);

      try {
        const activityData = await userService.getUserActivity(id, { limit: 10 });
        setActivity(activityData?.activities || activityData || []);
      } catch (activityError) {
        setActivity([]);
      }
    } catch (error) {
      showError('Erreur lors du chargement des données de l\'utilisateur');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordForm({ new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async () => {
    if (passwordForm.new_password.length < 6) {
      showError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      showError('Les mots de passe doivent correspondre');
      return;
    }

    try {
      await userService.changeUserPassword(id, { new_password: passwordForm.new_password });
      showSuccess('Mot de passe réinitialisé avec succès');
      setShowPasswordModal(false);
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe');
    }
  };

  const handleToggleActive = async () => {
    try {
      if (user.is_active) {
        await userService.deactivateUser(user.id);
        showSuccess('Utilisateur désactivé');
      } else {
        await userService.activateUser(user.id);
        showSuccess('Utilisateur activé');
      }
      fetchUserData();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors du changement de statut');
    }
  };

  const fetchSessions = async () => {
    try {
      setSessionsLoading(true);
      const sessionsData = await userService.getUserSessions(id);
      setSessions(sessionsData?.sessions || sessionsData || []);
    } catch (error) {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const PermissionBadge = ({ permission }) => {
    const permissionName = permission?.name || permission || '';
    const getPermissionColor = (permission) => {
      if (permission.includes('create')) return 'bg-green-100 text-green-800';
      if (permission.includes('view')) return 'bg-blue-100 text-blue-800';
      if (permission.includes('edit')) return 'bg-yellow-100 text-yellow-800';
      if (permission.includes('delete')) return 'bg-red-100 text-red-800';
      return 'bg-gray-100 text-gray-800';
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPermissionColor(permissionName)}`}>
        {permissionName}
      </span>
    );
  };

  useEffect(() => {
    if (activeTab === 'sessions' && canViewUser) {
      fetchSessions();
    }
  }, [activeTab, id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement de l'utilisateur...</p>
        </div>
      </div>
    );
  }

  if (!canViewUser) {
    return (
      <div className="card">
        <div className="p-6 text-center">
          <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Accès non autorisé</h3>
          <p className="mt-1 text-sm text-gray-500">
            Vous devez avoir la permission de gestion des utilisateurs pour consulter cet écran.
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Utilisateur non trouvé</h3>
        <p className="mt-1 text-sm text-gray-500">
          L'utilisateur que vous recherchez n'existe pas.
        </p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/users')}
            className="btn-primary"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Retour aux utilisateurs
          </button>
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
              <UserIcon className="h-10 w-10 text-primary-600" />
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-semibold text-gray-900">
                {user.first_name} {user.last_name}
              </h1>
              <div className="flex items-center mt-1">
                <p className="text-sm text-gray-500 mr-4">
                  @{user.username}
                </p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                `}>
                  {user.is_active ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          {canManageUsers && currentUser?.id !== user.id && (
            <>
              <button
                onClick={() => navigate(`/users/edit/${user.id}`)}
                className="btn-secondary"
              >
                Modifier
              </button>
              <button
                onClick={handleChangePassword}
                className="btn-primary"
              >
                <KeyIcon className="h-5 w-5 mr-2" />
                Changer mot de passe
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'activity', 'permissions', 'sessions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab === 'overview' && 'Aperçu'}
              {tab === 'activity' && 'Activité'}
              {tab === 'permissions' && 'Permissions'}
              {tab === 'sessions' && 'Sessions'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="py-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* User Info */}
            <div className="lg:col-span-2">
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Informations personnelles
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Nom complet</p>
                        <p className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-sm font-medium text-gray-900">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Téléphone</p>
                        <p className="text-sm font-medium text-gray-900">
                          {user.phone || 'Non renseigné'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Agence</p>
                        <p className="text-sm font-medium text-gray-900">
                          {user.agency_name || 'Non assigné'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Rôle</p>
                        <p className="text-sm font-medium text-gray-900">
                          {user.role_name || 'Non assigné'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Date de création</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(user.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Last Login */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500">Dernière connexion</p>
                        <p className="text-sm font-medium text-gray-900">
                          {user.last_login 
                            ? new Date(user.last_login).toLocaleString('fr-FR')
                            : 'Jamais'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Statistiques
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Activités (7j)</span>
                      <span className="text-sm font-medium text-gray-900">
                        {activity.length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Statut</span>
                      <span className={`text-sm font-medium ${user.is_active ? 'text-green-600' : 'text-red-600'}`}>
                        {user.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Membre depuis</span>
                      <span className="text-sm font-medium text-gray-900">
                        {Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))} jours
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Actions rapides
                  </h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate(`/users/edit/${user.id}`)}
                      className="w-full btn-secondary"
                      disabled={!canManageUsers || currentUser?.id === user.id}
                    >
                      Modifier le profil
                    </button>
                    <button
                      onClick={handleChangePassword}
                      className="w-full btn-secondary"
                      disabled={!canManageUsers || currentUser?.id === user.id}
                    >
                      Réinitialiser le mot de passe
                    </button>
                    {canManageUsers && currentUser?.id !== user.id && (
                      <button
                        onClick={handleToggleActive}
                        className="w-full btn-secondary text-red-600 border-red-300 hover:bg-red-50"
                      >
                        {user.is_active ? 'Désactiver' : 'Activer'} le compte
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="card">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Activité récente
                </h3>
              </div>

              {activity.length > 0 ? (
                <div className="flow-root">
                  <ul className="-mb-8">
                    {activity.map((item, index) => (
                      <li key={index}>
                        <div className="relative pb-8">
                          {index !== activity.length - 1 && (
                            <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" />
                          )}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                                <DocumentTextIcon className="h-4 w-4 text-white" />
                              </span>
                            </div>
                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                              <div>
                                <p className="text-sm text-gray-900">{item.description}</p>
                                <p className="text-sm text-gray-500">{item.action}</p>
                              </div>
                              <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                <div>{new Date(item.timestamp || item.created_at).toLocaleDateString('fr-FR')}</div>
                                <div>{new Date(item.timestamp || item.created_at).toLocaleTimeString('fr-FR')}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-12">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune activité</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Cet utilisateur n'a pas encore d'activité enregistrée.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'permissions' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Permissions du rôle
              </h3>
              
              {user.permissions && user.permissions.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {user.permissions.map((permission, index) => (
                      <PermissionBadge key={index} permission={permission} />
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">
                    {user.permissions.length} permission(s) au total
                  </p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune permission</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Cet utilisateur n'a aucune permission spécifique.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Sessions actives
              </h3>

              {sessionsLoading ? (
                <p className="text-sm text-gray-500">Chargement des sessions...</p>
              ) : sessions.length > 0 ? (
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Appareil</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse IP</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière activité</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sessions.map((session) => (
                        <tr key={session.id}>
                          <td className="px-4 py-3 text-sm text-gray-900">{session.device_type || 'Inconnu'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{session.ip_address || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {session.last_activity ? new Date(session.last_activity).toLocaleString('fr-FR') : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {session.is_current ? (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                Session courante
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                Active
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune session</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Aucune session active n'est disponible pour cet utilisateur.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900">Réinitialiser le mot de passe</h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nouveau mot de passe</label>
                <input
                  type="password"
                  className="input-field mt-1"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Confirmer le mot de passe</label>
                <input
                  type="password"
                  className="input-field mt-1"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button type="button" className="btn-secondary" onClick={() => setShowPasswordModal(false)}>
                Annuler
              </button>
              <button type="button" className="btn-primary" onClick={handlePasswordSubmit}>
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
